import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { KeyboardArrowDown as LoadMoreIcon } from '@mui/icons-material';
import type { AuditLog } from '@byte-store-control/shared';
import { AuditRow } from './AuditRow';

interface AuditTableProps {
  logs: AuditLog[];
  isLoading?: boolean;
  error?: string | null;
  showStoreColumn?: boolean;
  showUserColumn?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onStoreClick?: (storeNumber: string) => void;
  emptyMessage?: string;
}

export const AuditTable: React.FC<AuditTableProps> = ({
  logs,
  isLoading = false,
  error,
  showStoreColumn = true,
  showUserColumn = true,
  onLoadMore,
  hasMore = false,
  onStoreClick,
  emptyMessage = 'No activity found',
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleToggleExpand = (logId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!isLoading && logs.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 48 }} />
              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
              {showUserColumn && (
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              {showStoreColumn && (
                <TableCell sx={{ fontWeight: 600 }}>Store</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <AuditRow
                key={log.id}
                log={log}
                isExpanded={expandedRows.has(log.id)}
                onToggleExpand={() => handleToggleExpand(log.id)}
                showStoreColumn={showStoreColumn}
                showUserColumn={showUserColumn}
                onStoreClick={onStoreClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Loading indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Load More Button */}
      {!isLoading && hasMore && onLoadMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            startIcon={<LoadMoreIcon />}
          >
            Load More
          </Button>
        </Box>
      )}
    </Box>
  );
};
